import { axiosClient } from "@/lib/axios";
import { PublicRecording, WithPagination } from "@classview/shared-types";
import {
  UseQueryOptions,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";

const fetchRecordingsPage = async ({
  organisationId,
  pageSize = 10,
  pageParam = 0,
  search,
  sessionId,
  startDate,
  endDate,
  tags,
}: {
  organisationId?: string;
  pageSize?: number;
  pageParam?: number;
  search?: string;
  sessionId?: string;
  startDate?: string;
  endDate?: string;
  tags?: string[];
}): Promise<WithPagination<PublicRecording>> => {
  const offset = pageParam * pageSize;
  const response = await axiosClient.get(`${organisationId}/recordings`, {
    params: {
      limit: pageSize,
      offset,
      ...(search && { search: search }),
      ...(sessionId && { sessionId }),
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
      ...(tags && { tags }),
    },
  });
  return response.data;
};

const fetchRecordingsAll = async ({
  pageSize = 10,
  pageParam = 0,
  organisationId,
}: {
  pageSize: number;
  pageParam: number;
  organisationId: string;
}): Promise<WithPagination<PublicRecording>> => {
  const offset = pageParam * pageSize;
  const response = await axiosClient.get(`${organisationId}/recordings`, {
    params: {
      limit: pageSize,
      offset,
    },
  });

  return response.data;
};

const fetchRecordingsBySession = async ({
  sessionId,
}: {
  sessionId: string;
}): Promise<PublicRecording[]> => {
  const response = await axiosClient.get(`/recordings/session/${sessionId}`);
  return response.data;
};

export function fetchRecordingsQueryKey({
  organisationId,
  pageSize,
  search,
  sessionId,
  startDate,
  endDate,
  tags,
}: {
  organisationId: string;
  pageSize?: number;
  search?: string;
  sessionId?: string;
  startDate?: string;
  endDate?: string;
  tags?: string[];
}) {
  return [
    "recordings",
    organisationId,
    pageSize ?? "no-page-size",
    search ?? "no-search",
    sessionId ?? "no-session-id",
    startDate ?? "no-start-date",
    endDate ?? "no-end-date",
    tags ? tags.join(",") : "no-tags",
  ];
}

export function fetchRecordingsBySessionQueryKey({
  sessionId,
}: {
  sessionId: string;
}) {
  return ["recordings", sessionId];
}

export function useInfiniteRecordings({
  pageSize = 10,
  search,
  organisationId,
  sessionId,
  startDate,
  endDate,
  tags,
}: {
  pageSize?: number;
  search?: string;
  organisationId: string;
  sessionId?: string;
  startDate?: string;
  endDate?: string;
  tags?: string[];
}) {
  return useInfiniteQuery({
    queryKey: fetchRecordingsQueryKey({
      organisationId,
      pageSize,
      search,
      startDate,
      endDate,
      tags,
    }),
    initialPageParam: 0,
    queryFn: ({ pageParam = 0 }) =>
      fetchRecordingsPage({
        organisationId,
        pageSize,
        pageParam,
        search,
        sessionId,
        startDate,
        endDate,
        tags,
      }),
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      const totalPages = Math.ceil(lastPage.count / pageSize);
      return lastPageParam < totalPages - 1 ? lastPageParam + 1 : undefined;
    },
  });
}

export function useRecordings(
  {
    pageSize = 5,
    pageIndex = 0,
    organisationId,
  }: {
    pageSize?: number;
    pageIndex?: number;
    organisationId: string;
  },
  options?: Partial<UseQueryOptions<WithPagination<PublicRecording>>>,
) {
  return useQuery({
    queryKey: ["recordings", pageSize, pageIndex],
    queryFn: () =>
      fetchRecordingsAll({ pageSize, pageParam: pageIndex, organisationId }),
    ...options,
  });
}

export function useRecordingsBySessionId(
  {
    sessionId,
  }: {
    sessionId: string;
  },
  options?: Partial<UseQueryOptions<PublicRecording[]>>,
) {
  return useQuery({
    queryKey: fetchRecordingsBySessionQueryKey({ sessionId }),
    queryFn: () => fetchRecordingsBySession({ sessionId }),
    ...options,
  });
}
